import React, { useState, useEffect } from 'react';

function AteraTickets() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('Open');
  const [priority, setPriority] = useState('all');
  const [darkMode, setDarkMode] = useState(() => {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (priority !== 'all') params.append('priority', priority);
      params.append('pageSize', '50');
      
      const response = await fetch("/api/atera/tickets?" + params);
      const data = await response.json();
      
      if (data.error) {
        setError(data.message);
      } else {
        setTickets(data.tickets || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/atera/stats');
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Stats error:', err);
    }
  };

  if (loading) return <div className="p-4">Loading tickets...</div>;
  if (error) return <div className="p-4 text-red-500">Error: {error}</div>;

  // Check theme directly (more reliable than state)
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  
  const containerStyle = isDark ? {
    backgroundColor: '#111827',
    minHeight: '100vh'
  } : {};

  const tableStyle = isDark ? {
    backgroundColor: '#1f2937',
    color: '#f3f4f6'
  } : {};

  return (
    <div className="p-6" style={containerStyle}>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">🎫 Atera Tickets</h1>
        
        <button 
          onClick={() => { fetchTickets(); fetchStats(); }}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.openTickets || 0}</div>
            <div className="text-sm text-gray-500">Open</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{stats.criticalTickets || 0}</div>
            <div className="text-sm text-gray-500">Critical</div>
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <select 
          value={filter} 
          onChange={(e) => { setFilter(e.target.value); }}
          className="px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
        >
          <option value="all">All Status</option>
          <option value="Open">Open</option>
          <option value="Resolved">Resolved</option>
          <option value="Closed">Closed</option>
        </select>

        <select 
          value={priority} 
          onChange={(e) => { setPriority(e.target.value); }}
          className="px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
        >
          <option value="all">All Priority</option>
          <option value="Critical">Critical</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select>
      </div>

      {/* Tickets Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left">Ticket</th>
              <th className="px-4 py-3 text-left">Title</th>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Priority</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Created</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((ticket) => (
              <tr key={ticket.id} className="border-t hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700">
                <td className="px-4 py-3 font-medium dark:text-gray-200">{ticket.number}</td>
                <td className="px-4 py-3 dark:text-gray-300">{ticket.title ? ticket.title.substring(0, 40) : ''}</td>
                <td className="px-4 py-3 dark:text-gray-300">{ticket.customerName || '-'}</td>
                <td className="px-4 py-3 dark:text-gray-300">
                  <span className={"px-2 py-1 rounded text-xs font-medium " + (
                    ticket.priority === 'Critical' ? 'bg-red-100 text-red-700 dark:bg-red-700 dark:text-white ' :
                    ticket.priority === 'High' ? 'bg-orange-100 text-orange-700 dark:bg-orange-700 dark:text-white ' :
                    ticket.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-700 dark:text-white ' :
                    'bg-green-100 text-green-700 dark:bg-green-700 dark:text-white '
                  )}>
                    {ticket.priority}
                  </span>
                </td>
                <td className="px-4 py-3 dark:text-gray-300">
                  <span className={ticket.status === 'Open' ? 'text-blue-600' : 'text-gray-500'}>
                    {ticket.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500">
                  {ticket.createdDate ? new Date(ticket.createdDate).toLocaleDateString() : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {tickets.length === 0 && (
        <div className="p-8 text-center text-gray-500 bg-white rounded-lg">
          No tickets found
        </div>
      )}
    </div>
  );
}

export default AteraTickets;
